import { P1 } from '@/components/typography'
import Block from '@/layouts/block'
import { useId, useState } from 'react'
import { WithinPageNav } from '@/components/within-page-nav'
import { TabsContentValues } from '@/interfaces/stories'
import { FilterIcon } from '../icons/filter'
import { useSmallScreen } from '@/hooks/useMediaQuery'

type SingleTabProps = Omit<WithinPageNav, 'handleWithinPageNav'> & {
  text: TabsContentValues
  tabIndex: number
}

type TabsProps = Omit<WithinPageNav, 'handleWithinPageNav'> & {
  content: TabsContentValues[] | string[]
  className?: string
}

const SingleTab = ({
  activeElementIndex,
  handleChangeActiveElemIndex,
  text,
  tabIndex,
}: SingleTabProps) => {
  return (
    <button
      onClick={() => handleChangeActiveElemIndex(tabIndex)}
      className={`singleTab ${
        tabIndex === activeElementIndex
          ? `singleTab--active`
          : `singleTab--inactive`
      }`}
    >
      <P1>{text}</P1>
    </button>
  )
}

export const ResponsiveTabs = ({
  activeElementIndex,
  handleChangeActiveElemIndex,
  content,
  className = '',
}: TabsProps) => {
  const isSmallScreen = useSmallScreen()

  return isSmallScreen ? (
    <MobileTabs
      className={className}
      content={content}
      activeElementIndex={activeElementIndex}
      handleChangeActiveElemIndex={handleChangeActiveElemIndex}
    />
  ) : (
    <Tabs
      className={className}
      content={content}
      activeElementIndex={activeElementIndex}
      handleChangeActiveElemIndex={handleChangeActiveElemIndex}
    />
  )
}

export const MobileTabs = ({
  activeElementIndex,
  handleChangeActiveElemIndex,
  content,
  className = '',
}: TabsProps) => {
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false)

  const toggleFilter = () => setIsFilterOpen(!isFilterOpen)

  return (
    <>
      <button onClick={toggleFilter} className="flex gap-[10px] items-center">
        <FilterIcon />
        <P1 className="mobileTabs__filterText">Filters</P1>
      </button>
      {isFilterOpen && (
        <Tabs
          activeElementIndex={activeElementIndex}
          handleChangeActiveElemIndex={handleChangeActiveElemIndex}
          content={content}
          className={className}
        />
      )}
    </>
  )
}

const Tabs = ({
  activeElementIndex,
  handleChangeActiveElemIndex,
  content,
  className = '',
}: TabsProps) => {
  const id = useId()
  return (
    <Block className={`tabs ${className}`}>
      {content.map((text, index) => {
        return (
          <SingleTab
            key={`${id}--${index}`}
            activeElementIndex={activeElementIndex}
            handleChangeActiveElemIndex={handleChangeActiveElemIndex}
            text={text}
            tabIndex={index}
          />
        )
      })}
    </Block>
  )
}

export default Tabs
